import { ApacCheckoutComponent, useApacCheckoutStyles } from "@apac/checkout";
import {
  type PaymentFormValues,
  useAddPayment,
  useCvvIconName,
} from "@apac/components/checkout";
import { TextField } from "@apac/form-ui";
import { useModuleBEM } from "@b2bportal/core-themes";
import { useDeviceType } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { ICheckoutPaymentFormProps } from "@hopper-b2b/ui";
import { Icon, Loading } from "@tenant/ui";
import defaultStyles from "./AddPaymentForm.module.scss";

type AddPaymentFormProps = Pick<
  ICheckoutPaymentFormProps,
  "loading" | "onSubmit" | "onError" | "onClose"
> & {
  yearDigit?: "two" | "four";
};

export const AddPaymentForm: React.FC<AddPaymentFormProps> = (props) => {
  const { loading: paymentsLoading, onClose, yearDigit } = props;
  const { t, brand } = useI18nContext();
  const styles = useApacCheckoutStyles(
    ApacCheckoutComponent.CheckoutPaymentForm,
    defaultStyles
  );
  const { isMobile: mobile } = useDeviceType();
  const [block, cn] = useModuleBEM(
    styles,
    ApacCheckoutComponent.CheckoutPaymentForm,
    { mobile }
  );

  const {
    context: { formContext, addPaymentLoading },
    handlers: { onSubmitForm },
  } = useAddPayment(props);

  const cvvIconName = useCvvIconName();

  if (paymentsLoading || addPaymentLoading) {
    return <Loading />;
  }

  return (
    <form className={block} onSubmit={onSubmitForm}>
      <span className={cn("title")}>{t("apac.paymentForm.title")}</span>
      <span className={cn("description")}>
        {t("apac.paymentForm.description")}
      </span>

      <TextField<PaymentFormValues>
        formContext={formContext}
        name={"cardNumber"}
        label={t("paymentForm.cardNumber")}
        placeholder={t("paymentForm.cardNumberPlaceholder")}
        className={cn("card-number")}
      />

      <TextField<PaymentFormValues>
        formContext={formContext}
        name={"nameOnCard"}
        label={t("paymentForm.nameOnCard")}
        placeholder={t("paymentForm.nameOnCardPlaceholder")}
        className={cn("card-name")}
      />

      <div className={cn("date-and-cvv-container")}>
        <div className={cn("expiry-date")}>
          <TextField<PaymentFormValues>
            formContext={formContext}
            name={"expiryDate"}
            label={t("paymentForm.expiryDate")}
            placeholder={
              yearDigit === "four"
                ? t("paymentForm.expiryDatePlaceholder.four", {
                    defaultValue: "MM/YYYY",
                  })
                : t("paymentForm.expiryDatePlaceholder.two", {
                    defaultValue: "MM/YY",
                  })
            }
          />
        </div>

        <div className={cn("cvv")}>
          <TextField<PaymentFormValues>
            formContext={formContext}
            name={"cvv"}
            label={t("paymentForm.cvv")}
            placeholder={t("paymentForm.cvvPlaceholder")}
          />
        </div>

        <Icon className={cn("cvv-icon")} iconName={cvvIconName} />
      </div>

      {!brand.hidePaymentZipCode && (
        <TextField<PaymentFormValues>
          formContext={formContext}
          name={"zipCode"}
          label={t("paymentForm.zipCode")}
          placeholder={t("paymentForm.zipCodePlaceholder")}
          className={cn("zip-code")}
        />
      )}

      <button
        className={cn("submit-button")}
        type="submit"
        disabled={!formContext.formState.isValid}
      >
        {t("paymentForm.save", { defaultValue: t("save") })}
      </button>

      <button className={cn("close-button")} type="button" onClick={onClose}>
        {t("paymentForm.close", { defaultValue: t("close.button") })}
      </button>
    </form>
  );
};
